import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Reusable Dashboard Card Component
 * Consistent styling for all dashboard widgets
 */
export const DashboardCard = ({ 
  children, 
  className = '', 
  padding = 'p-4',
  noPadding = false 
}) => {
  return (
    <div className={cn(
      'bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800',
      'shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden',
      !noPadding && padding,
      className
    )}>
      {children}
    </div>
  );
};

/**
 * Card Header Component
 */
export const CardHeader = ({ 
  title, 
  icon: Icon, 
  action, 
  subtitle,
  className = '' 
}) => {
  return (
    <div className={cn('flex items-center justify-between mb-4', className)}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-500 dark:text-zinc-400" />}
        <div>
          <h3 className="text-xs font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
            {title}
          </h3>
          {subtitle && (
            <p className="text-[10px] text-slate-500 mt-0.5 dark:text-zinc-400">{subtitle}</p>
          )}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

/**
 * Empty State Component for cards with no data
 */
export const EmptyState = ({ 
  icon: Icon, 
  title, 
  description, 
  action,
  actionLabel,
  compact = false 
}) => {
  return (
    <div className={cn(
      'flex flex-col items-center justify-center text-center',
      compact ? 'py-4' : 'py-8'
    )}>
      {Icon && (
        <div className={cn(
          'rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-3',
          compact ? 'w-10 h-10' : 'w-12 h-12'
        )}>
          <Icon className={cn(
            'text-slate-400 dark:text-zinc-500',
            compact ? 'w-5 h-5' : 'w-6 h-6'
          )} />
        </div>
      )}
      <p className={cn(
        'font-medium text-slate-600 dark:text-zinc-400',
        compact ? 'text-xs' : 'text-sm'
      )}>
        {title}
      </p>
      {description && (
        <p className="text-xs text-slate-500 dark:text-zinc-500 mt-1 max-w-[200px]">
          {description}
        </p>
      )}
      {action && actionLabel && (
        <button
          onClick={action}
          className={cn(
            'mt-3 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg',
            'text-xs font-medium transition-colors'
          )}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};

export default DashboardCard;
